import { useMemo } from 'react';
import type { ColumnDef } from '@deckspace/types';
import { useSurfaceContext } from '../../components/surface/SurfaceContext';
import { Table } from '../shared/Table.js';
import type { TableMapping } from '../shared/table-utils.js';

interface DataTableViewProps {
  nodeId: string;
  mapping?: {
    visibleColumns?: string[];
    sortColumn?: string;
    sortDirection?: 'asc' | 'desc';
  };
  editable?: boolean;
  minimized?: boolean;
  // Injected by withDataset HOC (Task 8):
  rows: Record<string, unknown>[];
  columnDefs: ColumnDef[];
}

const EMPTY_CONFIG = {};

/**
 * Detect id-like columns by name pattern and value shape.
 * Matches: exactly 'id' (the auto-generated PK) and columns whose values are UUIDs.
 * Does NOT match '_id' suffix (e.g., workspace_id, user_id) — those are foreign keys
 * that may be meaningful to the user.
 */
export function isIdLikeColumn(
  column: string,
  rows: Record<string, unknown>[],
  colName: string
): boolean {
  if (column === 'id') return true;
  // Check first non-null value for UUID pattern
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  for (const row of rows.slice(0, 5)) {
    const val = row[colName];
    if (typeof val === 'string' && UUID_RE.test(val)) return true;
  }
  return false;
}

export function DataTableView({ mapping, rows, columnDefs }: DataTableViewProps) {
  const { themeKey } = useSurfaceContext();

  // Compute visibleColumns: prefer explicit mapping; otherwise hide id-like columns.
  const tableMapping = useMemo<TableMapping>(() => {
    const defs: ColumnDef[] = columnDefs ?? [];
    const rs: Record<string, unknown>[] = rows ?? [];
    let visibleColumns: string[] | undefined = mapping?.visibleColumns;
    if (!visibleColumns || visibleColumns.length === 0) {
      const allNames = defs.map((d) => d.name);
      const hidden = new Set(allNames.filter((c) => isIdLikeColumn(c, rs, c)));
      // Don't filter if ALL columns are id-like (edge case — show everything)
      if (hidden.size >= allNames.length) {
        visibleColumns = undefined;
      } else {
        visibleColumns = allNames.filter((c) => !hidden.has(c));
      }
    }
    return {
      visibleColumns,
      sortColumn: mapping?.sortColumn,
      sortDirection: mapping?.sortDirection,
    };
  }, [rows, columnDefs, mapping]);

  return (
    <Table
      key={themeKey}
      rows={rows}
      columnDefs={columnDefs}
      mapping={tableMapping}
      config={EMPTY_CONFIG}
    />
  );
}
